import _ from 'lodash';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { getDefaultErrorMessage } from 'utils/axios';
import {
  checkIsAdditionalLoanFlow,
  checkIsRefinanceFlow,
  getDownPaymentAmount,
  getDownPaymentPercent,
} from 'utils/loanApplication';

export const getAppraisedValue = ({ loanPurpose, propertyValue }) => {
  if (!loanPurpose || checkIsAdditionalLoanFlow(loanPurpose)) {
    return;
  }

  const appraisedValue = checkIsRefinanceFlow(loanPurpose) ? propertyValue : null;

  return { appraisedValue };
};

// Resolves the next {downPaymentValue, downPaymentPercent} pair off the Purchase Price -- no
// Loan Amount involvement (unlike the Loan Info editor's down payment pair; the lead/loan-creation
// form has no baseLoanAmount invariant to hold).
export const resolveLeadDownPaymentSync = ({
  hasPurchasePrice,
  hasDownPaymentPercentChanged,
  purchasePrice,
  downPaymentValue,
  downPaymentPercent,
}) => {
  if (!hasPurchasePrice) {
    return { downPaymentValue, downPaymentPercent: null };
  }

  if (hasDownPaymentPercentChanged) {
    return {
      downPaymentValue: getDownPaymentAmount({ percent: downPaymentPercent, purchasePrice }),
      downPaymentPercent,
    };
  }

  return {
    downPaymentValue,
    downPaymentPercent: getDownPaymentPercent({ amount: downPaymentValue, purchasePrice }),
  };
};

// react-number-format (v4) keeps showing its last value when its `value` prop turns null/undefined;
// only '' clears the input. So currency/percent fields are reset to '' (what the input itself emits
// when a user clears it), otherwise a stale number stays on screen while the form value is empty.
export const getClearedNumberFields = fieldNames =>
  Object.fromEntries(fieldNames.map(fieldName => [fieldName, '']));

// A synced value that should be empty is written as '' when the field currently shows a value (see
// getClearedNumberFields); an untouched (nil) field keeps its nil value.
export const getDisplayedNumberValue = ({ nextValue, currentValue }) =>
  _.isNil(nextValue) && !_.isNil(currentValue) ? '' : nextValue;

// A cleared number field holds '': treat it as "no value" for down payment math (Number('') is 0).
export const getSyncNumberValue = value => (value === '' ? null : value);

const CONVERT_AFTER_CREATE_FALLBACK_MESSAGE =
  'Lead was created but could not be converted to a loan. Open the lead to retry.';

// A 409 is a business refusal the server words for the user (e.g. the borrower already has a
// live loan at this property, naming that loan) — the generic retry hint would hide the reason.
export const getConvertAfterCreateErrorMessage = err =>
  err?.response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT
    ? getDefaultErrorMessage(err)
    : CONVERT_AFTER_CREATE_FALLBACK_MESSAGE;
